import { useState } from "react";

import carouselImage1 from "@/../../hypetrain/static/img/integration_flyout_carousel_image_1-min.jpg";
import carouselImage2 from "@/../../hypetrain/static/img/integration_flyout_carousel_image_2-min.jpg";
import { Box } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { WorkspaceAdminHeader } from "@/components/pages_logged_in/workspace-admin/WorkspaceAdminHeader";
import { getWorkspaceAdminTabs } from "@/components/pages_logged_in/workspace-admin/WorkspaceAdminTabs";
import { Container } from "@/components/pieces/bootstrap";
import { SectionTitle } from "@/components/pieces/text/SectionTitle";
import { Subtitle } from "@/components/pieces/text/Subtitle";
import { FirefliesLogo } from "@/components/ui/fireflies-logo";
import { IntegrationCard } from "@/components/ui/integration-card";
import {
  IntegrationFlyout,
  type IntegrationInstance,
} from "@/components/ui/integration-flyout";
import {
  type InputField,
  IntegrationModal,
} from "@/components/ui/integration-modal";
import { SIDE_NAV_WIDTH } from "@/css/constants";
import {
  useCreateFirefliesIntegrationMutation,
  useCurrentWorkspaceQuery,
  useDeleteFirefliesIntegrationMutation,
  useFirefliesIntegrationsQuery,
} from "@/graphql";

function WorkspaceIntegrationsInner() {
  const tabs = getWorkspaceAdminTabs();
  const [isFirefliesFlyoutOpen, setIsFirefliesFlyoutOpen] = useState(false);
  const [isFirefliesModalOpen, setIsFirefliesModalOpen] = useState(false);
  const [integrationName, setIntegrationName] = useState("");
  const [token, setToken] = useState("");
  const [connectionErrors, setConnectionErrors] = useState<string[]>([]);

  // Get current workspace
  const { data: workspaceData } = useCurrentWorkspaceQuery();
  const workspaceId = workspaceData?.currentWorkspace?.id;

  // GraphQL hooks
  const {
    data: integrationsData,
    loading: integrationsLoading,
    refetch: refetchIntegrations,
  } = useFirefliesIntegrationsQuery({
    variables: { workspaceId: workspaceId! },
    skip: !workspaceId,
  });

  const [createIntegration, { loading: createLoading }] =
    useCreateFirefliesIntegrationMutation();
  const [deleteIntegration, { loading: deleteLoading }] =
    useDeleteFirefliesIntegrationMutation();

  const integrations = integrationsData?.firefliesIntegrations || [];
  const hasIntegrations = integrations.length > 0;

  // Fireflies-specific carousel images
  const firefliesCarouselImages = [
    {
      src: carouselImage1,
      alt: "Automatically access all of your calls recordings",
    },
    {
      src: carouselImage2,
      alt: "Measure your team's performance in recorded meetings",
    },
  ];

  const firefliesNameInput: InputField = {
    id: "integration-name",
    label: "Name",
    type: "text",
    placeholder: "e.g., Sales Team, Marketing",
    value: integrationName,
    onChange: setIntegrationName,
    required: true,
  };

  const firefliesTokeyInput: InputField = {
    id: "api-token",
    label: "API Access Token",
    type: "text",
    placeholder: "Enter your Fireflies API key",
    value: token,
    onChange: setToken,
    required: true,
  };

  const firefliesToDescription = (
    <div className="flex flex-col gap-3">
      <p className="text-base font-normal leading-snug text-gray-800 m-0">
        Get an access token from your Fireflies workspace
      </p>

      <div className="text-base font-normal leading-relaxed">
        <ul className="list-disc ml-5 space-y-1">
          <li>
            Log in to your account at{" "}
            <a
              href="https://app.fireflies.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-600"
            >
              app.fireflies.ai
            </a>
          </li>
          <li>
            Navigate to the{" "}
            <a
              href="https://app.fireflies.ai/settings"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-600"
            >
              Settings
            </a>{" "}
            section
          </li>
          <li>Click on "Developer Settings"</li>
          <li>Copy your API key and input below</li>
        </ul>
      </div>
    </div>
  );

  return (
    <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
      <WorkspaceAdminHeader initialTab="integrations" tabs={tabs} />
      <Container>
        <Box mx={18} mt={22}>
          <Box mb={2}>
            <SectionTitle>Call Recorders</SectionTitle>
          </Box>
          <Box mb={4}>
            <Subtitle>Automatically import your calls into Exec</Subtitle>
          </Box>

          {/* Fireflies Integration - Single Card */}
          <IntegrationFlyout
            integrationName="Fireflies.ai"
            icon={<FirefliesLogo size={40} />}
            status={hasIntegrations ? "connected" : "disconnected"}
            statusLabel={
              hasIntegrations
                ? integrations.length > 1
                  ? `${integrations.length} Connected`
                  : "Connected"
                : "Not Connected"
            }
            description="Fireflies.ai automatically records, transcribes, and summarizes your calls from Zoom, Teams, and other platforms. Connect it to Exec to seamlessly import call recordings for AI-powered analysis and training."
            carouselImages={firefliesCarouselImages}
            instances={integrations
              .filter(
                (integration): integration is NonNullable<typeof integration> =>
                  integration != null,
              )
              .map(
                (integration): IntegrationInstance => ({
                  id: integration.id!,
                  name: integration.name || "Unnamed",
                  enabledBy: {
                    name:
                      integration.integratedBy?.firstName &&
                      integration.integratedBy?.lastName
                        ? `${integration.integratedBy.firstName} ${integration.integratedBy.lastName}`
                        : integration.integratedBy?.firstName ||
                          integration.integratedBy?.lastName ||
                          "Unknown User",
                    avatar: undefined, // No avatar field in current schema
                  },
                  enabledOn: integration.createdAt
                    ? new Date(integration.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        },
                      )
                    : "Unknown",
                }),
              )}
            open={isFirefliesFlyoutOpen}
            onOpenChange={setIsFirefliesFlyoutOpen}
            onConnect={() => {
              setConnectionErrors([]);
              setIntegrationName("");
              setToken("");
              setIsFirefliesModalOpen(true);
              // Don't close the flyout - keep it open
            }}
            onDisconnectInstance={async (instanceId) => {
              try {
                await deleteIntegration({
                  variables: { integrationId: instanceId },
                });
                await refetchIntegrations();
              } catch (error) {
                console.error("Failed to disconnect Fireflies:", error);
              }
            }}
            loading={createLoading || deleteLoading}
          >
            <IntegrationCard
              name="Fireflies.ai"
              icon={<FirefliesLogo size={40} />}
              status={hasIntegrations ? "connected" : "disconnected"}
              statusLabel={
                hasIntegrations
                  ? integrations.length > 1
                    ? `${integrations.length} Connected`
                    : "Connected"
                  : "Not Connected"
              }
              onConnect={
                hasIntegrations
                  ? undefined
                  : () => setIsFirefliesFlyoutOpen(true)
              }
              onManage={
                hasIntegrations
                  ? () => setIsFirefliesFlyoutOpen(true)
                  : undefined
              }
              loading={integrationsLoading || createLoading}
            />
          </IntegrationFlyout>

          <IntegrationModal
            open={isFirefliesModalOpen}
            onOpenChange={setIsFirefliesModalOpen}
            integrationName="Fireflies"
            icon={<FirefliesLogo size={40} />}
            description={firefliesToDescription}
            inputs={[firefliesNameInput, firefliesTokeyInput]}
            loading={createLoading}
            errors={connectionErrors}
            onConnect={async (values) => {
              if (!workspaceId) return;

              // Clear previous errors
              setConnectionErrors([]);

              try {
                const result = await createIntegration({
                  variables: {
                    workspaceId: workspaceId!,
                    name: values["integration-name"],
                    apiKey: values["api-token"],
                  },
                });

                if (result.data?.createFirefliesIntegration?.ok) {
                  setIsFirefliesModalOpen(false);
                  setIntegrationName("");
                  setToken("");
                  await refetchIntegrations();
                  // Keep the flyout open to show the new instance
                } else {
                  const errors =
                    result.data?.createFirefliesIntegration?.errors || [];
                  const errorMessages = errors
                    .map((error) => error?.message)
                    .filter((message): message is string => Boolean(message));
                  setConnectionErrors(errorMessages);
                }
              } catch (error) {
                console.error("Failed to create integration:", error);
                setConnectionErrors([
                  "An unexpected error occurred. Please try again.",
                ]);
              }
            }}
            onCancel={() => {
              setIsFirefliesModalOpen(false);
              setIntegrationName("");
              setToken("");
              setConnectionErrors([]);
              // Keep the flyout open
            }}
          />
        </Box>
      </Container>
    </Box>
  );
}

export function WorkspaceIntegrations() {
  return (
    <Authenticate
      requireWorkspaceAgent={true}
      requireWorkspaceAdmin={true}
      maxWidth="100%"
      layout="dashboard"
    >
      <Nav />
      <WorkspaceIntegrationsInner />
    </Authenticate>
  );
}
